import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { cn } from '../../lib/cn';
import { formatILS } from './quote.schema';
import styles from './quotes.module.css';

interface PublicLineItem {
  service_label: string;
  description: string | null;
  quantity: number;
  unit_price: number;
  subtotal: number;
}

interface PublicQuote {
  id: string;
  quote_number: string;
  status: string;
  customer_name: string | null;
  value_amount: number;
  value_currency: string;
  valid_until: string | null;
  notes: string | null;
  items: PublicLineItem[];
  org_name: string;
}

function getFunctionBase(): string {
  const url = import.meta.env.VITE_SUPABASE_URL;
  return `${url}/functions/v1/quote-public`;
}

export function QuoteShareView() {
  const { id } = useParams<{ id: string }>();
  const [quote, setQuote] = useState<PublicQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${getFunctionBase()}?id=${encodeURIComponent(id)}`);
        if (!res.ok) {
          if (!active) return;
          setError(res.status === 404 ? 'ההצעה לא נמצאה' : 'תקלה בטעינה');
          setLoading(false);
          return;
        }
        const data = (await res.json()) as PublicQuote;
        if (!active) return;
        setQuote(data);
      } catch {
        if (active) setError('תקלה בטעינה · נסה שוב');
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [id]);

  async function act(action: 'accept' | 'reject') {
    if (!id || acting) return;
    if (action === 'reject' && !window.confirm('לדחות את ההצעה?')) return;
    setActing(true);
    try {
      const res = await fetch(getFunctionBase(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, action }),
      });
      if (!res.ok) {
        setError('לא הצלחנו לעדכן · רענן ונסה שוב');
        return;
      }
      const updated = (await res.json()) as PublicQuote;
      setQuote(updated);
    } catch {
      setError('לא הצלחנו לעדכן · רענן ונסה שוב');
    } finally {
      setActing(false);
    }
  }

  if (loading) {
    return <div className={styles.shareView} dir="rtl"><p className={styles.shareLoading}>טוען...</p></div>;
  }

  if (error || !quote) {
    return (
      <div className={styles.shareView} dir="rtl">
        <p className={styles.shareError}>{error ?? 'תקלה'}</p>
      </div>
    );
  }

  const isFinal = quote.status === 'accepted' || quote.status === 'rejected' || quote.status === 'expired';

  return (
    <div className={styles.shareView} dir="rtl">
      <header className={styles.shareHead}>
        <div className={styles.shareBrand}>{quote.org_name}</div>
        <div className={styles.shareQuoteNumber}>
          <bdi dir="ltr">{quote.quote_number}</bdi>
        </div>
      </header>

      <div className={styles.shareSummary}>
        <div className={styles.shareCustomer}>{quote.customer_name ?? 'לקוח'}</div>
        {quote.valid_until ? (
          <div className={styles.shareValidity}>
            בתוקף עד {format(new Date(quote.valid_until), 'dd/MM/yyyy')}
          </div>
        ) : null}
      </div>

      <div className={styles.shareLineItems}>
        <div className={styles.itemRowHead}>
          <span>שירות</span>
          <span>כמות</span>
          <span>מחיר יחידה</span>
          <span>סכום</span>
        </div>
        {quote.items.map((item, idx) => (
          <div key={idx} className={styles.shareLineItem}>
            <span className={styles.itemLabel}>{item.service_label}</span>
            <span><bdi>{item.quantity}</bdi></span>
            <span><bdi>{formatILS(item.unit_price)}</bdi></span>
            <span className={styles.subtotal}><bdi>{formatILS(item.subtotal)}</bdi></span>
            {item.description ? (
              <span className={styles.shareLineDescription}>{item.description}</span>
            ) : null}
          </div>
        ))}
      </div>

      <div className={styles.shareTotal}>
        <span>סה"כ</span>
        <span><bdi>{formatILS(quote.value_amount)}</bdi></span>
      </div>

      {quote.notes ? <div className={styles.shareNotes}>{quote.notes}</div> : null}

      {quote.status === 'accepted' ? (
        <div className={cn(styles.shareStatusBanner, styles['shareStatusBanner--accepted'])}>
          ההצעה אושרה. תודה!
        </div>
      ) : null}
      {quote.status === 'rejected' ? (
        <div className={cn(styles.shareStatusBanner, styles['shareStatusBanner--rejected'])}>
          ההצעה נדחתה.
        </div>
      ) : null}
      {quote.status === 'expired' ? (
        <div className={cn(styles.shareStatusBanner, styles['shareStatusBanner--expired'])}>
          ההצעה פגה. צור קשר כדי לקבל הצעה חדשה.
        </div>
      ) : null}

      {!isFinal ? (
        <div className={styles.shareActions}>
          <button
            type="button"
            className={styles.shareBtnAccept}
            disabled={acting}
            onClick={() => { void act('accept'); }}
          >
            אשר הצעה
          </button>
          <button
            type="button"
            className={styles.shareBtnReject}
            disabled={acting}
            onClick={() => { void act('reject'); }}
          >
            דחה
          </button>
        </div>
      ) : null}
    </div>
  );
}
